import {
  AllFlowsDefinition,
  FlowAction,
  FlowDefinition,
  FunnelState,
  FormAnswer,
  MoveFlowAction,
  PaymentDetailsData,
  ReviewDetailsData,
} from 'scenes/common/types';
import { HomeFormValues } from 'scenes/home/hooks/useActions/types';
import { HomeApiResponses } from 'scenes/home/actions/types';

import { HomeQuoteData, HomeQuoteSubSection } from './quoteDetails/types';
import { HomePolicySubSection, PolicyDetailsData } from './policyDetails/types';
import { CoverOptionsData, HomeCoverSubSection } from './coverOptions/types';

export enum HomeFlowType {
  QuoteDetails = 'quoteDetails',
  CoverOptions = 'coverOptions',
  PolicyDetails = 'policyDetails',
  PaymentDetails = 'paymentDetails',
  ReviewDetails = 'reviewDetails',
}

export enum HomeStepType {
  CoverTypeSelection = 'cover-type-selection',
  PolicyStartDate = 'policy-start-date',
  RiskAddress = 'risk-address',
  Ownership = 'ownership',
  PropertyPurpose = 'property-purpose',
  PropertyUsedForBusiness = 'property-used-for-business',
  PropertyBusinessType = 'property-business-type',
  KickOutChoice = 'kick-out-choice',
  PersonalOrBusinessPolicy = 'personal-business-policy',
  ContactDateOfBirth = 'contact-date-of-birth',
  RecentInsurer = 'recent-insurer',
  YearsInsured = 'years-insured',
  ClaimsMade = 'claims-made',
  BuildingType = 'building-type',
  BreakInAndAdditionalSecurity = 'break-in-and-additional-security',
  NumberOfBedrooms = 'number-of-bedrooms',
  PropertyBuiltYear = 'property-built-year',
  RoofMaterial = 'roof-material',
  WallMaterial = 'wall-material',
  SecurityFeatures = 'security-features',
  SumInsuredChoice = 'estimated-sum-insured-choice',
  SumInsuredBuildingValue = 'building-sum-insured',
  SumInsuredContentsValue = 'contents-sum-insured',
  FlybuysNumber = 'flybuys-number',
  PromoCode = 'promo-code',
  QuotePrice = 'quote-price',
  BuildingExcess = 'building-excess',
  ContentsExcess = 'contents-excess',
  OptionalExtras = 'optional-extras',
  SpecifiedContentsAnswer = 'items-above-policy-limit-answer',
  SpecifiedContentList = 'items-above-policy-limit-list',
  AddSpecifiedContent = 'add-item-above-policy-limit',
  UnspecifiedPortableContents = 'unspecified-portable-contents',
  PropertyCondition = 'property-condition',
  HeritageListed = 'heritage-listed',
  RewiredOrReplumbed = 'rewired-or-replumbed',
  PropertyUnoccupied = 'property-unoccupied',
  PropertyRenovation = 'property-renovation',
  Over5Acres = 'over-five-acres',
  PropertyOccupancy = 'property-occupancy',
  CashKeptAtHome = 'cash-kept-at-home',
  StockKeptAtHome = 'stock-kept-at-home',
  HazardGoodsKeptAtHome = 'hazard-goods-kept-at-home',
  NumberOfVisits = 'number-of-visits',
  BusinessRegisteredForGst = 'business-registered-for-gst',
  OccupantsHistory = 'occupants-history',
  MortgageProvider = 'mortgage-provider',
  BusinessNameAndAbn = 'business-name-and-abn',
  BusinessContact = 'business-contact',
  PersonalPolicyContact = 'personal-policy-contact',
  ContactEmailAddress = 'contact-email-address',
  ContactPhoneNumber = 'contact-phone-number',
  AddAdditionalPolicyHolder = 'add-additional-policy-holder',
  AdditionalPolicyHolder = 'additional-policy-holder',
  AdditionalPolicyHolderList = 'additional-policy-holder-list',
  PostalAddress = 'postal-address',
  DifferentPostalAddress = 'different-postal-address',
  DifferentPostalAddressManual = 'different-postal-address-manual',
  LockedBagAddress = 'locked-bag-address',
  MarketingConsent = 'marketing-consent',
  TermsAndConditions = 'terms-and-conditions',
  Summary = 'summary',
  PaymentFrequency = 'payment-frequency',
  PaymentDate = 'payment-date',
  PaymentMethod = 'payment-method',
  DirectDebitPayment = 'direct-debit-payment',
  CreditCardPayment = 'credit-card-payment',
  PaymentConfirmation = 'payment-confirmation',
  ReviewPolicyStartDate = 'review-policy-start-date',
}

export enum HomeMoveFlows {
  EditQuoteDetails = 'editQuoteDetails',
  EditCoverDetails = 'editCoverDetails',
  EditPolicyDetails = 'editPolicyDetails',
}

export type HomeFunnelState = FunnelState<HomeFlowType, HomeStepType> & {
  [HomeFlowType.QuoteDetails]: HomeQuoteData;
  [HomeFlowType.CoverOptions]: CoverOptionsData;
  [HomeFlowType.PolicyDetails]: PolicyDetailsData;
  [HomeFlowType.PaymentDetails]: PaymentDetailsData;
  [HomeFlowType.ReviewDetails]: ReviewDetailsData;
  apiResponses: HomeApiResponses;
};

export type HomeFlowDefinition = FlowDefinition<
  HomeFormValues,
  HomeFlowType,
  HomeStepType,
  HomeFunnelState
>;
export type HomeAllFlowDefinition = AllFlowsDefinition<
  HomeFormValues,
  HomeFlowType,
  HomeStepType,
  HomeFunnelState
>;
export type HomeFlowAction = FlowAction<HomeFormValues, HomeStepType>;
export type HomeFormAnswer = FormAnswer<HomeFormValues, HomeStepType>;
export type HomeMoveFlowAction = MoveFlowAction<
  HomeFormValues,
  HomeMoveFlows,
  HomeStepType
>;

export type HomeSubSection =
  | HomeQuoteSubSection
  | HomePolicySubSection
  | HomeCoverSubSection;

export enum HomeSection {
  QuoteDetails = 'Quote details',
  CoverDetails = 'Cover details',
  PolicyDetails = 'Policy details',
}
